'use client'

import { AuthProvider, useAuth } from '@/providers/Auth'
import { EcommerceProvider } from '@payloadcms/plugin-ecommerce/client/react'
import React from 'react'

import { HeaderThemeProvider } from './HeaderTheme'
import { ThemeProvider } from './Theme'
import { SonnerProvider } from '@/providers/Sonner'
import { EnhancedCartProvider } from '@/providers/EnhancedCart'
import { bankTransferAdapterClient, cashEnvelopeAdapterClient, pickupAdapterClient } from '@/payments'
import { AnimationProvider } from '@/providers/Animation'

const EcommerceProviderWithAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()

  return (
    <EcommerceProvider
      key={user?.id || 'guest'}
      currenciesConfig={{
        defaultCurrency: 'EUR',
        supportedCurrencies: [
          {
            code: 'EUR',
            decimals: 2,
            label: 'Euro',
            symbol: '€',
          },
        ],
      }}
      enableVariants={true}
      api={{
        serverURL: process.env.NEXT_PUBLIC_SERVER_URL!,
        cartsFetchQuery: {
          depth: 2,
          populate: {
            products: {
              slug: true,
              title: true,
              gallery: true,
              basePrice: true,
              priceInEUR: true,
              categories: true,
              weightKg: true,
            },
            variants: {
              title: true,
              options: true,
              priceInEUR: true,
              priceInEUREnabled: true,
            },
          },
        },
      }}
      paymentMethods={[
        // Custom manual payment methods for CBD products (no Stripe - ToS prohibits CBD)
        bankTransferAdapterClient,
        cashEnvelopeAdapterClient,
        pickupAdapterClient,
      ]}
    >
      <EnhancedCartProvider>{children}</EnhancedCartProvider>
    </EcommerceProvider>
  )
}

export const Providers: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  return (
    <AnimationProvider>
      <ThemeProvider>
        <AuthProvider>
          <HeaderThemeProvider>
            <SonnerProvider />
            <EcommerceProviderWithAuth>{children}</EcommerceProviderWithAuth>
          </HeaderThemeProvider>
        </AuthProvider>
      </ThemeProvider>
    </AnimationProvider>
  )
}
